import React, { useEffect, FC, useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom';
import mediumZoom from 'medium-zoom';
import { createStyles, makeStyles } from '@material-ui/core';
import { ExtendedTheme } from 'neon-design-system';
import { isInViewport } from '../../utils/element-position.utils'
import { LogAction, LogLevel } from '../providers/logger/logger-types'
import { useLogger } from '../providers/logger/Logger'
import { useUser } from '../providers/UserProvider';
import Player from '@vimeo/player';
import PreventCopyPaste from './PreventCopyPaste';
import Watermark from './Watermark';
import { setBuzzsproutPlayerHeight } from '../../utils/utils';
import { isChartsArticle, makeCharts } from '../../utils/tableau';
import { useApolloClient } from '@apollo/react-hooks';
interface ArticleProps {
	id: string;
	title: string;
	html: string;
	showWatermark?: boolean;
	watermarkText?: string;
	isMarketing?: boolean;
}

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) => createStyles({
	articleContent: {
		'& a': {
			color: theme.palette.secondary.main,
			textDecoration: 'none',
			'&:hover': {
				textDecoration: 'none',
				color: theme.themeColors.secondary[700]
			},
			'&:focus': {
				textDecoration: 'none'
			},
			'& > u': {
				textDecoration: 'none'
			}
		},
		'& > :first-child': {
			marginTop: '0 !important'
		},
		'& h1': {
			margin: `${theme.spacing(3)}px 0 ${theme.spacing(1)}px 0`,
			...theme.customTypography.title1.medium,
			[theme.breakpoints.up('xs')]: {
				...theme.customTypography.title1.small
			},
			[theme.breakpoints.up('md')]: {
				...theme.customTypography.title1.medium
			},
			[theme.breakpoints.up('xl')]: {
				...theme.customTypography.title1.large
			}
		},
		'& h2': {
			margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
			...theme.customTypography.headline1.medium,
			[theme.breakpoints.up('xs')]: {
				...theme.customTypography.headline1.small
			},
			[theme.breakpoints.up('md')]: {
				...theme.customTypography.headline1.medium
			},
			[theme.breakpoints.up('xl')]: {
				...theme.customTypography.headline1.large
			}
		},
		'& h3': {
			margin: `${theme.spacing(2)}px 0 ${theme.spacing(1)}px 0`,
			...theme.customTypography.headline2.medium,
			[theme.breakpoints.up('xs')]: {
				...theme.customTypography.headline2.small
			},
			[theme.breakpoints.up('md')]: {
				...theme.customTypography.headline2.medium
			},
			[theme.breakpoints.up('xl')]: {
				...theme.customTypography.headline2.large
			}
		},
		'& p, & li, & ol, & p + p': {
			[theme.breakpoints.up('xl')]: {
				...theme.customTypography.body.large
			},
			margin: `${theme.spacing(1)}px 0`,
			...theme.customTypography.body.medium
		},
		'& span.fr-img-caption.fr-dii': {
			[theme.breakpoints.down('md')]: {
				display: 'flex',
				width: 'auto !important'
			}
		},
		'& span.fr-img-caption.fr-dib': {
			[theme.breakpoints.down('md')]: {
				display: 'flex',
				width: 'auto !important'
			}
		},
		'& .fr-img-caption': {
			width: '100%',
			[theme.breakpoints.down('md')]: {
				margin: '0 auto !important'
			},
			'& img': {
				width: 'auto !important'
			}
		},
		'& .fr-img-space-wrap': {
			...theme.typography.body1,
			[theme.breakpoints.down('md')]: {
				display: 'flex',
				justifyContent: 'center',
				flexDirection: 'column',
				fontSize: theme.customTypography.body.small.fontSize,
				lineHeight: theme.customTypography.body.large.lineHeight
			}
		},
		'& img': {
			borderRadius: 4,
			'@media print': {
				pageBreakInside: 'avoid'
			}
		},
		'& .fr-img-caption .fr-img-wrap > span': theme.typography.caption,
		[theme.breakpoints.down('md')]: {
			marginBottom: theme.spacing(4)
		},
		'& .promotional-content > img': {
			display: 'block',
			width: '100%',
			height: 'auto',
			borderRadius: '8px',
			transition: 'opacity .3s ease-out'
		},
		'& .promotional-content:hover > img': {
			opacity: 0.8
		},
		[theme.breakpoints.down('xs')]: {
			'& .promotional-content': {
				display: 'none'
			}
		},
		'& > .download-xls-spectrometer-report-block': {
			display: 'flex',
			justifyContent: 'space-between',
			background: theme.themeColors.blueGrey[700],
			padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
			borderRadius: 4,
			'& > div:first-child': {
				display: 'flex',
				flexDirection: 'column',
				'& > h2': {
					fontSize: 16,
					color: theme.palette.common.white,
					marginBottom: 0,
					marginTop: theme.spacing(1),
					[theme.breakpoints.down('sm')]: {
						fontSize: 14,
						lineHeight: theme.customTypography.body.large.lineHeight
					}

				},
				'& > p': {
					fontSize: 12,
					marginTop: 0,
					color: theme.themeColors.blueGrey[200],
					lineHeight: theme.customTypography.body.large.lineHeight,
					[theme.breakpoints.down('sm')]: {
						fontSize: 11,
						lineHeight: theme.customTypography.body.large.lineHeight
					}
				}
			},
			'& > div:last-child': {
				display: 'flex',
				alignItems: 'center',
				'& > button': {
					cursor: 'pointer',
					outline: 'none',
					border: 'none',
					boxShadow: 'none',
					fontSize: 12,
					lineHeight: theme.customTypography.body.large.lineHeight,
					fontWeight: 600,
					background: theme.componentColors.button.secondary.backgroundColor.main,
					color: theme.palette.common.white,
					padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
					borderRadius: 3,
					'&:hover, &:active': {
						background: theme.componentColors.button.secondary.backgroundColor.hover
					},
					[theme.breakpoints.down('xs')]: {
						width: '100%',
						marginTop: theme.spacing(1)
					}
				}
			},
			[theme.breakpoints.down('xs')]: {
				flexDirection: 'column',
				padding: theme.spacing(2)
			}
		},
		'@media print': {
			color: theme.themeColors.grey[900]
		}
	},
	articleContentCharts: {
		[theme.breakpoints.up('md')]: {
			'& > :not([data-id=tableau-viz])': {
				maxWidth: 720
			}
		},
		[theme.breakpoints.up('lg')]: {
			'& > :not([data-id=tableau-viz])': {
				maxWidth: 920
			}
		}
	}
}));

const ArticleContent: FC<ArticleProps> = ({ html, title, id, children, showWatermark, isMarketing, watermarkText }) => {
	const classes = useStyles();
	const history = useHistory();
	const [mountedTimestamp, setMountedTimestamp] = useState(0);
	const [loggedPromotionalContentIds, setLoggedPromotionalContentIds] = useState(new Set())
	const [loggedVideoUrls, setLoggedVideoUrls] = useState(new Set())
	const { printLog } = useLogger()
	const { userDetails } = useUser()
	const client = useApolloClient();

	const isInViewportHandler = useCallback(() => {
		document
			.querySelectorAll<HTMLLinkElement>('.promotional-content')
			.forEach((element) => {
				if (isInViewport<HTMLLinkElement>(element)) {
					const promotionalContentName = element.getAttribute('data-promotional-name')
					const promotionalContentId = element.getAttribute('data-promotional-id')

					if (!loggedPromotionalContentIds.has(promotionalContentId)) {
						printLog(
							LogLevel.INFO,
							LogAction.BodyImpression,
							'Promotional Content',
							{
								name: promotionalContentName,
								id: promotionalContentId,
								articleName: title,
								articleId: id,
								userId: userDetails?.id,
								whenMs: Date.now() - mountedTimestamp
							}
						);

						setLoggedPromotionalContentIds((prev) => prev.add(promotionalContentId))
					}
				}
			})
	}, [loggedPromotionalContentIds, printLog, title, id, userDetails, mountedTimestamp]);

	const clickHandler = useCallback((e) => {
		const { currentTarget } = e
		const promotionalContentName = currentTarget.getAttribute('data-promotional-name')
		const promotionalContentId = currentTarget.getAttribute('data-promotional-id')
		const promotionalContentLink = currentTarget.getAttribute('href')
		const promotionalContentTarget = currentTarget.getAttribute('target')

		printLog(
			LogLevel.INFO,
			LogAction.BodyClick,
			'Promotional Content',
			{
				name: promotionalContentName,
				id: promotionalContentId,
				articleName: title,
				articleId: id,
				userId: userDetails?.id,
				whenMs: Date.now() - mountedTimestamp
			}
		)

		if (!promotionalContentTarget) {
			e.preventDefault()
			history.push(promotionalContentLink)
		}
	}, [history, id, mountedTimestamp, printLog, title, userDetails]);

	const videoPlayHandler = useCallback((videoWrapper) => {
		const iframe: HTMLIFrameElement | null = videoWrapper.querySelector('iframe')

		if (iframe && iframe?.src.includes('vimeo')) {
			const videoUrl = iframe.src;
			const player = new Player(iframe)

			player.on('play', async () => {
				if (!loggedVideoUrls.has(videoUrl)) {
					const videoName = await player.getVideoTitle();

					printLog(
						LogLevel.INFO,
						LogAction.VideoStarted,
						'Embedded Video',
						{
							videoName,
							videoUrl,
							articleName: title,
							articleId: id,
							whenMs: Date.now() - mountedTimestamp
						}
					);

					setLoggedVideoUrls((prev) => prev.add(videoUrl))
				}
			});
		}
	}, [id, loggedVideoUrls, mountedTimestamp, printLog, title])

	useEffect(() => {
		isInViewportHandler()
		window.addEventListener('scroll', isInViewportHandler)
		return () => window.removeEventListener('scroll', isInViewportHandler, false);
	}, [isInViewportHandler])

	useEffect(() => {
		const promotionalContentLinks = document.querySelectorAll<HTMLLinkElement>('.promotional-content');

		promotionalContentLinks.forEach((element) => {
			element.addEventListener('click', clickHandler)
		})

		return () => promotionalContentLinks.forEach((element) => {
			element.removeEventListener('click', clickHandler, false)
		})
	}, [clickHandler])

	useEffect(() => {
		document.querySelectorAll('.fr-video').forEach(videoPlayHandler)
	}, [videoPlayHandler])

	useEffect(() => {
		setMountedTimestamp(Date.now())
	}, [])

	useEffect(() => {
		setMountedTimestamp(Date.now())
	}, [])

	useEffect(() => {
		setBuzzsproutPlayerHeight();
		const images = document.getElementById('article-content')?.querySelectorAll('img:not(.promotional-content-image)');

		if (images) {
			const imagesArray = [...images] as [HTMLImageElement];

			imagesArray.forEach((image) => {
				mediumZoom(image, {
					background: 'rgba(0, 0, 0, 0.85)'
				})
			});
		}
	}, [html])

	useEffect(() => {
		const downloadXlsButton = document.getElementById('download-xls-spectrometer-report-button');
		const downloadReportLink = downloadXlsButton?.getAttribute('data-xls-link');

		if (downloadXlsButton && downloadReportLink) {
			const downloadReportButtonClickHandler = async () => {
				window.open(`https:${downloadReportLink}`, '_blank');

				printLog(
					LogLevel.INFO,
					LogAction.DownloadData,
					'Excel',
					{
						fileLink: downloadReportLink,
						articleName: title,
						articleId: id,
						userId: userDetails?.id
					}
				)
			};

			downloadXlsButton.addEventListener('click', downloadReportButtonClickHandler);

			return downloadXlsButton.addEventListener('click', downloadReportButtonClickHandler, false);
		}
	}, [html, id, mountedTimestamp, printLog, title, userDetails])

	const chartsArticle = isChartsArticle(html);

	const articleContent = (<div
		id="article-content"
		className={`fr-view ${classes.articleContent} ${chartsArticle ? classes.articleContentCharts : ''}`}
		ref={(content) => content && makeCharts(client, content)}
		dangerouslySetInnerHTML={{ __html: html ?? '' }}>
		{children}
	</div>)

	const content = showWatermark ? (
		<Watermark watermarkText={watermarkText}>{articleContent}</Watermark>
	) : articleContent

	return isMarketing ? (content) : (<PreventCopyPaste>{content}</PreventCopyPaste>);
}

export default ArticleContent;
